import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageSquare } from "lucide-react";
import { NetworkBackground } from "./NetworkBackground";
import { TID } from "../constants/testIds";

export function Hero({ t, onPrimary, onSecondary }) {
  const stagger = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
  };
  const item = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <section id="top" className="relative min-h-[100svh] w-full overflow-hidden">
      <NetworkBackground />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-[140px] pb-24 md:pt-[180px] md:pb-32">
        <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-4xl">
          <motion.div variants={item} className="flex items-center gap-3 mb-8">
            <span className="inline-block w-8 h-px bg-[#00E5FF]" />
            <span
              className="text-[11px] uppercase tracking-[0.28em] text-[#00E5FF] font-semibold"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <motion.h1
            variants={item}
            className="text-white font-bold leading-[1.02] tracking-tighter"
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: "clamp(40px, 7vw, 84px)",
            }}
          >
            {t.hero.headline_1}
            <br />
            <span className="text-[#9BA1A6]">{t.hero.headline_2}</span>
            <span className="inline-block w-[10px] h-[0.85em] bg-[#00E5FF] ml-2 align-middle blink-cursor" />
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-8 text-base md:text-lg max-w-2xl leading-relaxed"
            style={{ color: "#9BA1A6", fontFamily: "'Manrope', sans-serif" }}
          >
            {t.hero.subheadline}
          </motion.p>

          <motion.div variants={item} className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid={TID.heroPrimary}
              onClick={onPrimary}
              className="group inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00C2D6] text-[#0B132B] font-bold px-8 py-4 rounded-sm transition-all duration-300"
              style={{
                fontFamily: "'Manrope', sans-serif",
                letterSpacing: "0.06em",
                boxShadow: "0 0 25px rgba(0,229,255,0.25)",
              }}
            >
              {t.hero.cta_primary}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid={TID.heroSecondary}
              onClick={onSecondary}
              className="inline-flex items-center gap-2 border border-[#00E5FF]/60 text-[#00E5FF] hover:bg-[#00E5FF]/10 font-bold px-8 py-4 rounded-sm transition-all duration-300"
              style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "0.06em" }}
            >
              <MessageSquare size={16} />
              {t.hero.cta_secondary}
            </button>
          </motion.div>

          <motion.div
            variants={item}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-3xl border-t border-white/10 pt-8"
          >
            {[
              { v: "5+", l: t.hero.stat_a },
              { v: "50k+", l: t.hero.stat_b },
              { v: "10+", l: t.hero.stat_c },
              { v: "5+", l: t.hero.stat_d },
            ].map((s, i) => (
              <div key={i}>
                <div
                  className="text-3xl md:text-4xl font-bold text-white"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {s.v}
                </div>
                <div
                  className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#9BA1A6]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
